import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface CraftResult {
  success: boolean;
  cost: { entries: { package: string; ref_key: string; amount: { value: number } }[] };
  reward: { entries: { package: string; ref_key: string; amount: { value: number } }[] };
  quality: string | null;
}

export default function Crafting({ session }: Props) {
  const [recipes, setRecipes] = useState<string[]>([]);
  const [selected, setSelected] = useState('');
  const [skillLevel, setSkillLevel] = useState(5);
  const [stations, setStations] = useState('anvil');
  const [result, setResult] = useState<CraftResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`/api/v1/games/${session.gameId}/modules/crafting/recipes`)
      .then(r => r.json())
      .then((d: { recipes: string[] }) => {
        setRecipes(d.recipes);
        if (d.recipes.length > 0 && !selected) setSelected(d.recipes[0]);
      })
      .catch(e => setError(e.message));
  }, [session.gameId]);

  const doCraft = async () => {
    if (!selected) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await fetch(
        `/api/v1/games/${session.gameId}/modules/crafting/craft/${selected}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            skill_level: skillLevel,
            stations: stations.split(',').map(s => s.trim()).filter(Boolean),
          }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      setResult(await res.json());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container>
      <PageHeader
        title="Crafting"
        subtitle="Test recipes — see costs consumed and rewards produced."
      />

      {error && <div className="error-banner">{error}</div>}

      <div className="card mb-5">
        <h3 className="text-sm font-semibold m-0 mb-3">Craft a recipe</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Recipe</label>
            <select className="input" value={selected} onChange={e => setSelected(e.target.value)}>
              {recipes.length === 0 && <option value="">No recipes published</option>}
              {recipes.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Skill level</label>
            <input className="input" type="number" value={skillLevel} onChange={e => setSkillLevel(parseInt(e.target.value, 10) || 0)} style={{ width: 100 }} />
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">Stations (comma-sep)</label>
            <input className="input" value={stations} onChange={e => setStations(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !selected} onClick={doCraft}>
            {busy ? 'Crafting…' : 'Craft'}
          </button>
        </div>
        <div className="text-xs text-fg-muted mt-3">
          Create recipes in Config Explorer → Crafting → Recipe, publish, then test here.
        </div>
      </div>

      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold m-0">Result</h3>
              <span className={`badge ${result.success ? 'badge-published' : 'badge-draft'}`}>
                {result.success ? 'Success' : 'Failed'}
              </span>
            </div>
            {result.quality && (
              <div className="text-sm mb-2">Quality: <strong>{result.quality}</strong></div>
            )}
            <h4 className="text-[11px] uppercase tracking-wider text-fg-dim font-semibold mt-3 mb-2">Cost consumed</h4>
            {result.cost.entries.length === 0 ? (
              <div className="text-xs text-fg-muted">No cost</div>
            ) : (
              <ul className="list-none p-0 m-0 flex flex-col gap-1">
                {result.cost.entries.map((e, i) => (
                  <li key={i} className="text-sm">
                    <span className="badge badge-muted text-[10px] mr-1">{e.package}</span>
                    {e.ref_key}: <strong>{e.amount.value}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold m-0 mb-3">Reward produced</h3>
            {result.reward.entries.length === 0 ? (
              <div className="text-xs text-fg-muted">No reward</div>
            ) : (
              <ul className="list-none p-0 m-0 flex flex-col gap-1">
                {result.reward.entries.map((e, i) => (
                  <li key={i} className="text-sm">
                    <span className="badge badge-muted text-[10px] mr-1">{e.package}</span>
                    {e.ref_key}: <strong>{e.amount.value}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {recipes.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No recipes published</div>
          <div className="text-xs text-fg-muted">Go to Config Explorer → Crafting → Recipe → + New</div>
        </div>
      )}
    </Container>
  );
}
